import type { ElementRegistry } from "../core/element-registry";
import { ContainerRenderer } from "./container-renderer";
import { TextRenderer } from "./text-renderer";
import { ImageRenderer } from "./image-renderer";
import { IframeRenderer } from "./iframe-renderer";
import { VideoRenderer } from "./video-renderer";
import { ButtonRenderer } from "./button-renderer";
import { BadgeRenderer } from "./badge-renderer";
import { IconRenderer } from "./icon-renderer";
import { CardRenderer } from "./card-renderer";
import { InputRenderer } from "./input-renderer";
import { InputGroupAddonRenderer } from "./input-group-addon-renderer";
import { SeparatorRenderer } from "./separator-renderer";
import { SkeletonRenderer } from "./skeleton-renderer";
import { PlaceholderRenderer } from "./placeholder-renderer";
import { TabsRenderer } from "./tabs-renderer";
import { TabTriggerRenderer } from "./tab-trigger-renderer";
import { BlockRefRenderer } from "./block-ref-renderer";
import {
  ShapeRenderer,
  SourceApplicationRenderer,
  SourceScreenRenderer,
} from "./source-structural-renderers";
// Phase 1: Form renderers
import { LabelRenderer } from "./label-renderer";
import { TextareaRenderer } from "./textarea-renderer";
import { CheckboxRenderer } from "./checkbox-renderer";
import { RadioRenderer } from "./radio-renderer";
import { SwitchRenderer } from "./switch-renderer";
import { SliderRenderer } from "./slider-renderer";
import { SelectRenderer } from "./select-renderer";
import { FormRenderer } from "./form-renderer";
// Phase 2: Overlay & Feedback renderers
import { DialogRenderer } from "./dialog-renderer";
import { ModalRenderer } from "./modal-renderer";
import { AlertDialogRenderer } from "./alert-dialog-renderer";
import {
  AlertDialogActionElementRenderer,
  AlertDialogCancelElementRenderer,
  AlertDialogContentElementRenderer,
} from "./alert-dialog-control-renderers";
import { SheetRenderer } from "./sheet-renderer";
import { DrawerRenderer } from "./drawer-renderer";
import { PopoverRenderer } from "./popover-renderer";
import { DropdownMenuRenderer } from "./dropdown-menu-renderer";
import { ContextMenuRenderer } from "./context-menu-renderer";
import { AlertRenderer } from "./alert-renderer";
import {
  ProgressIndicatorRenderer,
  ProgressRenderer,
} from "./progress-renderer";
import { ToastRenderer } from "./toast-renderer";
import { NavigationMenuRenderer } from "./navigation-menu-renderer";
// Phase 3: Data Display renderers
import { AccordionRenderer } from "./accordion-renderer";
import { AccordionItemRenderer } from "./accordion-item-renderer";
import { TableRenderer } from "./table-renderer";
import {
  TableBodyStructureRenderer,
  TableFooterStructureRenderer,
  TableHeaderStructureRenderer,
  TableRowStructureRenderer,
} from "./table-structure-renderers";
import { DataTableRenderer } from "./data-table-renderer";
import { ChartRenderer } from "./chart-renderer";
import { CarouselRenderer } from "./carousel-renderer";
import { MapRenderer } from "./map-renderer";
// Phase 4: Missing renderers (discovered via zero-base verification)
import { ListRenderer } from "./list-renderer";
import { AvatarRenderer, AvatarGroupRenderer } from "./avatar-renderer";
import { LinkRenderer } from "./link-renderer";
// Phase 6: Interactive renderers
import { TooltipRenderer } from "./tooltip-renderer";
// Phase 7: Navigation & Form renderers
import { BreadcrumbRenderer } from "./breadcrumb-renderer";
import { PaginationRenderer } from "./pagination-renderer";
import { DatePickerRenderer } from "./date-picker-renderer";
import { CalendarRenderer } from "./calendar-renderer";
import { ComboboxRenderer } from "./combobox-renderer";
// Phase 8: Feedback & Collapsible renderers
import { CollapsibleRenderer } from "./collapsible-renderer";
// Phase 9: Advanced interactive renderers
import { CommandRenderer } from "./command-renderer";
import { HoverCardRenderer } from "./hover-card-renderer";
import { InputOtpRenderer } from "./input-otp-renderer";
import { MenubarRenderer } from "./menubar-renderer";
import { ResizableRenderer } from "./resizable-renderer";
import { ToggleRenderer } from "./toggle-renderer";
import { ToggleGroupRenderer } from "./toggle-group-renderer";
// Phase 10: Flutter-exclusive interactive renderers
import { MonthPickerRenderer } from "./month-picker-renderer";
import { StarRatingRenderer } from "./star-rating-renderer";
import { ColorPickerRenderer } from "./color-picker-renderer";
import { ChipInputRenderer } from "./chip-input-renderer";
import { PhoneInputRenderer } from "./phone-input-renderer";
import { StepperRenderer } from "./stepper-renderer";
import { TimelineRenderer } from "./timeline-renderer";
import { TreeRenderer } from "./tree-renderer";
// Phase 11: Sidebar renderers
import {
  SidebarRenderer,
  SidebarInsetRenderer,
  SidebarTriggerRenderer,
} from "./sidebar-renderer";
// Phase 12: Semantic renderers
import { FieldRenderer } from "./field-renderer";
import { FieldGroupRenderer } from "./field-group-renderer";
import { InputGroupRenderer } from "./input-group-renderer";
import { EmptyRenderer } from "./empty-renderer";
import { ItemDescriptionRenderer, ItemRenderer } from "./item-renderer";
import { KbdRenderer } from "./kbd-renderer";
import { ButtonGroupRenderer } from "./button-group-renderer";
import { NativeSelectRenderer } from "./native-select-renderer";
import {
  RadialProgressRenderer,
  RingProgressRenderer,
} from "./ring-progress-renderer";
import { WaveformRenderer } from "./waveform-renderer";
// Phase 13: Theme-v2 M2 deferred atoms (loader / aspect-ratio / scroll-area)
import { LoaderRenderer } from "./loader-renderer";
import { AspectRatioRenderer } from "./aspect-ratio-renderer";
import { ScrollAreaRenderer } from "./scroll-area-renderer";
// Phase 14: Lottie (post-M2) — final deferred atom, lazy-loaded runtime
import { LottieRenderer } from "./lottie-renderer";
import {
  RadioGroupItemRenderer,
  RadioGroupRenderer,
} from "./radio-group-renderer";
import {
  BrandLogoRenderer,
  CartLineRenderer,
  CartLinesRenderer,
  CheckoutStepRenderer,
  CommerceButtonRenderer,
  ConfirmationDisplayRenderer,
  CopyrightRenderer,
  HeroBannerRenderer,
  ImageGalleryRenderer,
  LinkColumnsRenderer,
  LogoRenderer,
  MenuItemRenderer,
  MenuListRenderer,
  PriceDisplayRenderer,
  ProductCardRenderer,
  SemanticHeadingRenderer,
  SocialIconsRenderer,
  StepIndicatorRenderer,
  TotalsSummaryRenderer,
} from "./semantic-property-renderers";
// Slot renderer factory
import { createSlotRenderer, createSlotTextRenderer } from "./slot-renderer";
// Feature-island renderer (Phase 3 shell-as-cogo-app Prereq D)
import { IslandRenderer } from "./island-renderer";
// shadcn compound components for slot registration
import {
  CardHeader,
  CardDescription,
  CardAction,
  CardContent,
  CardFooter,
} from "../components/ui/card";
import {
  ItemHeader,
  ItemFooter,
  ItemContent,
  ItemTitle,
  ItemActions,
  ItemMedia,
  ItemSeparator,
} from "../components/ui/item";
import {
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  EmptyDescription,
  EmptyContent,
} from "../components/ui/empty";
import { AlertTitle, AlertDescription } from "../components/ui/alert";
import {
  FieldLabel,
  FieldContent,
  FieldDescription,
} from "../components/ui/field";
import {
  ButtonGroupSeparator,
  ButtonGroupText,
} from "../components/ui/button-group";
import { TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { TableHead, TableCell } from "../components/ui/table";
import { ToggleGroupItem } from "../components/ui/toggle-group";
import {
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuGroup,
  DropdownMenuLabel,
} from "../components/ui/dropdown-menu";
import {
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuGroup,
  ContextMenuLabel,
} from "../components/ui/context-menu";
import { SelectTrigger, SelectContent } from "../components/ui/select";
import {
  AlertDialogTrigger,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
} from "../components/ui/alert-dialog";
import { DialogTrigger, DialogContent } from "../components/ui/dialog";
import { PopoverTrigger, PopoverContent } from "../components/ui/popover";
import { SheetTrigger, SheetContent } from "../components/ui/sheet";
import {
  CollapsibleTrigger,
  CollapsibleContent,
} from "../components/ui/collapsible";
import { AccordionTrigger, AccordionContent } from "../components/ui/accordion";

export function registerAllRenderers(registry: ElementRegistry): void {
  // Source-derived application snapshots use structural element kinds that are
  // not regular shadcn atoms but must render through the same Studio runtime.
  registry.register("application", SourceApplicationRenderer);
  registry.register("screen", SourceScreenRenderer);
  registry.register("shape", ShapeRenderer);
  // Existing renderers
  registry.register("container", ContainerRenderer);
  registry.register("section", ContainerRenderer);
  // HTML5 semantic element aliases — render as container with appropriate htmlTag
  registry.register("header", ContainerRenderer);
  registry.register("footer", ContainerRenderer);
  registry.register("nav", ContainerRenderer);
  registry.register("main", ContainerRenderer);
  registry.register("aside", ContainerRenderer);
  registry.register("article", ContainerRenderer);
  registry.register("text", TextRenderer);
  // Heading has its own ontology contract: text and authored children are both
  // content, while level selects the native h1-h6 element.
  registry.register("heading", SemanticHeadingRenderer);
  registry.register("image", ImageRenderer);
  registry.register("iframe", IframeRenderer);
  registry.register("video", VideoRenderer);
  registry.register("button", ButtonRenderer);
  registry.register("badge", BadgeRenderer);
  registry.register("icon", IconRenderer);
  registry.register("card", CardRenderer);
  registry.register("input", InputRenderer);
  registry.register("separator", SeparatorRenderer);
  registry.register("divider", SeparatorRenderer);
  registry.register("skeleton", SkeletonRenderer);
  registry.register("placeholder", PlaceholderRenderer);
  registry.register("tabs", TabsRenderer);
  registry.register("tab-trigger", TabTriggerRenderer);
  registry.register("block-ref", BlockRefRenderer);
  // Feature-island renderer (Phase 3 shell-as-cogo-app Prereq D)
  registry.register("island", IslandRenderer);
  // Phase 1: Form renderers
  registry.register("label", LabelRenderer);
  registry.register("textarea", TextareaRenderer);
  registry.register("checkbox", CheckboxRenderer);
  registry.register("radio", RadioRenderer);
  registry.register("switch", SwitchRenderer);
  registry.register("slider", SliderRenderer);
  registry.register("select", SelectRenderer);
  registry.register("form", FormRenderer);
  // Phase 2: Overlay & Feedback renderers
  registry.register("dialog", DialogRenderer);
  registry.register("alert-dialog", AlertDialogRenderer);
  registry.register("sheet", SheetRenderer);
  registry.register("drawer", DrawerRenderer);
  registry.register("popover", PopoverRenderer);
  registry.register("dropdown-menu", DropdownMenuRenderer);
  registry.register("context-menu", ContextMenuRenderer);
  registry.register("alert", AlertRenderer);
  registry.register("progress", ProgressRenderer);
  registry.register("radial-progress", RadialProgressRenderer);
  registry.register("toast", ToastRenderer);
  registry.register("navigation-menu", NavigationMenuRenderer);
  // Phase 3: Data Display renderers
  registry.register("accordion", AccordionRenderer);
  registry.register("accordion-item", AccordionItemRenderer);
  registry.register("table", TableRenderer);
  registry.register("data-table", DataTableRenderer);
  registry.register("data_table", DataTableRenderer); // snake_case alias (some blocks use this)
  registry.register("chart", ChartRenderer);
  registry.register("carousel", CarouselRenderer);
  registry.register("map", MapRenderer);
  // Phase 4: Missing renderers
  registry.register("list", ListRenderer);
  registry.register("list_view", ListRenderer);
  registry.register("avatar", AvatarRenderer);
  registry.register("avatar-group", AvatarGroupRenderer);
  // Phase 5: Navigation
  registry.register("link", LinkRenderer);
  // Phase 6: Interactive renderers
  registry.register("tooltip", TooltipRenderer);
  // Phase 7: Navigation & Form renderers
  registry.register("breadcrumb", BreadcrumbRenderer);
  registry.register("pagination", PaginationRenderer);
  registry.register("date-picker", DatePickerRenderer);
  registry.register("calendar", CalendarRenderer);
  registry.register("combobox", ComboboxRenderer);
  // Phase 8: Feedback & Collapsible renderers
  registry.register("collapsible", CollapsibleRenderer);
  // Phase 9: Advanced interactive renderers
  registry.register("command", CommandRenderer);
  registry.register("hover-card", HoverCardRenderer);
  registry.register("input-otp", InputOtpRenderer);
  registry.register("menubar", MenubarRenderer);
  registry.register("resizable", ResizableRenderer);
  registry.register("toggle", ToggleRenderer);
  registry.register("toggle-group", ToggleGroupRenderer);
  // Phase 10: Flutter-exclusive interactive renderers
  registry.register("star-rating", StarRatingRenderer);
  registry.register("color-picker", ColorPickerRenderer);
  registry.register("chip-input", ChipInputRenderer);
  registry.register("phone-input", PhoneInputRenderer);
  registry.register("stepper", StepperRenderer);
  registry.register("timeline", TimelineRenderer);
  registry.register("tree", TreeRenderer);
  // Phase 10: Month picker
  registry.register("month-picker", MonthPickerRenderer);
  // Phase 11: Sidebar
  registry.register("sidebar", SidebarRenderer);
  registry.register("sidebar-inset", SidebarInsetRenderer);
  registry.register("sidebar-trigger", SidebarTriggerRenderer);
  // Phase 12: Semantic renderers
  registry.register("field", FieldRenderer);
  registry.register("field-group", FieldGroupRenderer);
  registry.register("input-group", InputGroupRenderer);
  registry.register("empty", EmptyRenderer);
  registry.register("item", ItemRenderer);
  registry.register("item-group", ItemRenderer);
  registry.register("kbd", KbdRenderer);
  registry.register("button-group", ButtonGroupRenderer);
  registry.register("native-select", NativeSelectRenderer);
  registry.register("ring-progress", RingProgressRenderer);
  registry.register("waveform", WaveformRenderer);
  // Phase 13: Theme-v2 M2 deferred atoms (consume default-theme.json defaults)
  registry.register("loader", LoaderRenderer);
  registry.register("aspect-ratio", AspectRatioRenderer);
  registry.register("scroll-area", ScrollAreaRenderer);
  // Phase 14: Lottie (post-M2) — closes canonical atom coverage 46→47/47
  registry.register("lottie", LottieRenderer);

  // Semantic aliases that are valid ontology element types and must never
  // fall through to ElementRenderer's unknown-type placeholder.
  registry.register("headline", SemanticHeadingRenderer);
  registry.register("subheadline", TextRenderer);
  registry.register("brand-logo", BrandLogoRenderer);
  registry.register("logo", LogoRenderer);
  registry.register("copyright", CopyrightRenderer);
  registry.register("hero-banner", HeroBannerRenderer);
  registry.register("product-card", ProductCardRenderer);
  registry.register("image-gallery", ImageGalleryRenderer);
  registry.register("price-display", PriceDisplayRenderer);
  registry.register("order-review", ContainerRenderer);
  registry.register("step-indicator", StepIndicatorRenderer);
  registry.register("confirmation-display", ConfirmationDisplayRenderer);
  registry.register("cart-line", CartLineRenderer);
  registry.register("cart-lines", CartLinesRenderer);
  registry.register("checkout-step", CheckoutStepRenderer);
  registry.register("link-columns", LinkColumnsRenderer);
  registry.register("menu-list", MenuListRenderer);
  registry.register("social-icons", SocialIconsRenderer);
  registry.register("totals-summary", TotalsSummaryRenderer);
  registry.register("menu-item", MenuItemRenderer);
  registry.register("modal", ModalRenderer);
  registry.register("cta-button", CommerceButtonRenderer);
  registry.register("add-to-cart-button", CommerceButtonRenderer);
  registry.register("checkout-cta", CommerceButtonRenderer);
  registry.register("empty-action", ButtonRenderer);
  registry.register("sonner", ToastRenderer);

  // ── Slot renderers: type → shadcn React component 1:1 ──
  // No internal transformation. className pass-through. Children recursive.
  const slot = createSlotRenderer;
  const slotText = createSlotTextRenderer;

  // Accordion
  registry.register("accordion-trigger", slotText(AccordionTrigger));
  registry.register("accordion-content", slot(AccordionContent));

  // Card
  registry.register("card-header", slot(CardHeader));
  registry.register("card-title", SemanticHeadingRenderer);
  registry.register("card-description", slotText(CardDescription));
  registry.register("card-action", slot(CardAction));
  registry.register("card-content", slot(CardContent));
  registry.register("card-footer", slot(CardFooter));
  // Item
  registry.register("item-header", slot(ItemHeader));
  registry.register("item-content", slot(ItemContent));
  registry.register("item-title", slotText(ItemTitle));
  registry.register("item-description", ItemDescriptionRenderer);
  registry.register("item-actions", slot(ItemActions));
  registry.register("item-media", slot(ItemMedia));
  registry.register("item-separator", slot(ItemSeparator));
  registry.register("item-footer", slot(ItemFooter));
  // Empty
  registry.register("empty-header", slot(EmptyHeader));
  registry.register("empty-icon", slot(EmptyMedia, { variant: "icon" }));
  registry.register("empty-media", slot(EmptyMedia));
  registry.register("empty-title", slotText(EmptyTitle));
  registry.register("empty-description", slotText(EmptyDescription));
  registry.register("empty-content", slot(EmptyContent));
  // Alert
  registry.register("alert-title", slotText(AlertTitle));
  registry.register("alert-description", slotText(AlertDescription));
  // Field
  registry.register("field-label", slotText(FieldLabel));
  registry.register("field-description", slotText(FieldDescription));
  registry.register("field-content", slot(FieldContent));
  registry.register("field-set", ContainerRenderer);
  registry.register("field-legend", slotText(FieldLabel));
  // Input group
  registry.register("input-group-addon", InputGroupAddonRenderer);
  // Tabs (shadcn components, not ContainerRenderer)
  registry.register("tabs-list", slot(TabsList));
  registry.register("tabs-trigger", slotText(TabsTrigger));
  registry.register("tabs-content", slot(TabsContent));
  // Table
  registry.register("table-header", TableHeaderStructureRenderer);
  registry.register("table-body", TableBodyStructureRenderer);
  registry.register("table-row", TableRowStructureRenderer);
  registry.register("table-head", slotText(TableHead));
  registry.register("table-cell", slotText(TableCell));
  registry.register("table-footer", TableFooterStructureRenderer);
  // Select
  registry.register("select-trigger", slot(SelectTrigger));
  registry.register("select-content", slot(SelectContent));
  // Toggle group
  registry.register("toggle-group-item", slotText(ToggleGroupItem));
  // Radio group
  registry.register("radio-group", RadioGroupRenderer);
  registry.register("radio-group-item", RadioGroupItemRenderer);
  // Native select
  registry.register("native-select-wrapper", ContainerRenderer);
  // Dropdown menu
  // slotText (not slot): the trigger commonly carries a `text` label
  // (e.g. {type:"dropdown-menu-trigger", text:"Actions"}); plain slot drops text
  // and renders an empty 0×0 button. Matches alert-dialog-trigger. Children
  // (icon/button) still render after the text, so children-only usage is unchanged.
  registry.register("dropdown-menu-trigger", slotText(DropdownMenuTrigger));
  registry.register("dropdown-menu-content", slot(DropdownMenuContent));
  registry.register("dropdown-menu-item", slotText(DropdownMenuItem));
  registry.register("dropdown-menu-separator", slot(DropdownMenuSeparator));
  registry.register("dropdown-menu-group", slot(DropdownMenuGroup));
  registry.register("dropdown-menu-label", slotText(DropdownMenuLabel));
  // Context menu slots (mirror dropdown-menu: trigger/item/label carry text → slotText)
  registry.register("context-menu-trigger", slotText(ContextMenuTrigger));
  registry.register("context-menu-content", slot(ContextMenuContent));
  registry.register("context-menu-item", slotText(ContextMenuItem));
  registry.register("context-menu-separator", slot(ContextMenuSeparator));
  registry.register("context-menu-group", slot(ContextMenuGroup));
  registry.register("context-menu-label", slotText(ContextMenuLabel));
  // Dialog slots (compound components). dialog-renderer.tsx composes these
  // from children by type; the registry entries cover direct dispatch so an
  // orphaned slot degrades like the alert-dialog slots instead of falling to
  // <span data-unknown-type>.
  registry.register("dialog-trigger", slotText(DialogTrigger));
  registry.register("dialog-content", slot(DialogContent));
  // Popover / Sheet / Collapsible slots. Their parent renderers already
  // type-match these children (popover-renderer.tsx, sheet-renderer.tsx,
  // collapsible-renderer.tsx); until now the names were undeclared, so the
  // typed arm was unreachable from the generation surface. Registered through
  // the same factories as every other declared slot pair, and REQUIRED by
  // scripts/ingest-ui-elements.ts, which emits an unconditional
  // "React: supported" renderability fact for every declared type.
  registry.register("popover-trigger", slotText(PopoverTrigger));
  registry.register("popover-content", slot(PopoverContent));
  registry.register("sheet-trigger", slotText(SheetTrigger));
  registry.register("sheet-content", slot(SheetContent));
  registry.register("collapsible-trigger", slotText(CollapsibleTrigger));
  registry.register("collapsible-content", slot(CollapsibleContent));
  // Alert dialog slots (compound components)
  registry.register("alert-dialog-trigger", slotText(AlertDialogTrigger));
  registry.register("alert-dialog-content", AlertDialogContentElementRenderer);
  registry.register("alert-dialog-header", slot(AlertDialogHeader));
  registry.register("alert-dialog-footer", slot(AlertDialogFooter));
  registry.register("alert-dialog-title", slotText(AlertDialogTitle));
  registry.register(
    "alert-dialog-description",
    slotText(AlertDialogDescription),
  );
  registry.register("alert-dialog-action", AlertDialogActionElementRenderer);
  registry.register("alert-dialog-cancel", AlertDialogCancelElementRenderer);
  // Button group
  registry.register("button-group-separator", slot(ButtonGroupSeparator));
  registry.register("button-group-text", slotText(ButtonGroupText));
  // Progress
  registry.register("progress-indicator", ProgressIndicatorRenderer);
}

export { ElementRenderer } from "./element-renderer";
export { ContainerRenderer } from "./container-renderer";
export { TextRenderer } from "./text-renderer";
export { ImageRenderer } from "./image-renderer";
export { IframeRenderer } from "./iframe-renderer";
export { VideoRenderer } from "./video-renderer";
export { ButtonRenderer } from "./button-renderer";
export { BadgeRenderer } from "./badge-renderer";
export { IconRenderer } from "./icon-renderer";
export { CardRenderer } from "./card-renderer";
export { InputRenderer } from "./input-renderer";
export { SeparatorRenderer } from "./separator-renderer";
export { SkeletonRenderer } from "./skeleton-renderer";
export { PlaceholderRenderer } from "./placeholder-renderer";
export { LabelRenderer } from "./label-renderer";
export { TextareaRenderer } from "./textarea-renderer";
export { CheckboxRenderer } from "./checkbox-renderer";
export { RadioRenderer } from "./radio-renderer";
export { SwitchRenderer } from "./switch-renderer";
export { SliderRenderer } from "./slider-renderer";
export { SelectRenderer } from "./select-renderer";
export { FormRenderer } from "./form-renderer";
export { DialogRenderer } from "./dialog-renderer";
export { ModalRenderer } from "./modal-renderer";
export { AlertDialogRenderer } from "./alert-dialog-renderer";
export { SheetRenderer } from "./sheet-renderer";
export { DrawerRenderer } from "./drawer-renderer";
export { PopoverRenderer } from "./popover-renderer";
export { DropdownMenuRenderer } from "./dropdown-menu-renderer";
export { ContextMenuRenderer } from "./context-menu-renderer";
export { AlertRenderer } from "./alert-renderer";
export {
  ProgressIndicatorRenderer,
  ProgressRenderer,
} from "./progress-renderer";
export { ToastRenderer } from "./toast-renderer";
export { NavigationMenuRenderer } from "./navigation-menu-renderer";
export { AccordionRenderer } from "./accordion-renderer";
export { AccordionItemRenderer } from "./accordion-item-renderer";
export { TableRenderer } from "./table-renderer";
export { DataTableRenderer } from "./data-table-renderer";
export { ChartRenderer } from "./chart-renderer";
export { CarouselRenderer } from "./carousel-renderer";
export { ListRenderer } from "./list-renderer";
export { AvatarRenderer } from "./avatar-renderer";
export { LinkRenderer } from "./link-renderer";
export { TooltipRenderer } from "./tooltip-renderer";
export { BreadcrumbRenderer } from "./breadcrumb-renderer";
export { PaginationRenderer } from "./pagination-renderer";
export { DatePickerRenderer } from "./date-picker-renderer";
export { CalendarRenderer } from "./calendar-renderer";
export { ComboboxRenderer } from "./combobox-renderer";
export { CollapsibleRenderer } from "./collapsible-renderer";
export { CommandRenderer } from "./command-renderer";
export { HoverCardRenderer } from "./hover-card-renderer";
export { InputOtpRenderer } from "./input-otp-renderer";
export { MenubarRenderer } from "./menubar-renderer";
export { ResizableRenderer } from "./resizable-renderer";
export { ToggleRenderer } from "./toggle-renderer";
export { ToggleGroupRenderer } from "./toggle-group-renderer";
export { StarRatingRenderer } from "./star-rating-renderer";
export { ColorPickerRenderer } from "./color-picker-renderer";
export { ChipInputRenderer } from "./chip-input-renderer";
export { PhoneInputRenderer } from "./phone-input-renderer";
export { StepperRenderer } from "./stepper-renderer";
export { TimelineRenderer } from "./timeline-renderer";
export { TreeRenderer } from "./tree-renderer";
export { MonthPickerRenderer } from "./month-picker-renderer";
export {
  SidebarRenderer,
  SidebarInsetRenderer,
  SidebarTriggerRenderer,
} from "./sidebar-renderer";
// Phase 12: Semantic renderers
export { FieldRenderer } from "./field-renderer";
export { FieldGroupRenderer } from "./field-group-renderer";
export { InputGroupRenderer } from "./input-group-renderer";
export { EmptyRenderer } from "./empty-renderer";
export { ItemRenderer } from "./item-renderer";
export { KbdRenderer } from "./kbd-renderer";
export { ButtonGroupRenderer } from "./button-group-renderer";
export { NativeSelectRenderer } from "./native-select-renderer";
// Phase 13: Theme-v2 M2 deferred atoms
export { LoaderRenderer } from "./loader-renderer";
export { AspectRatioRenderer } from "./aspect-ratio-renderer";
export { ScrollAreaRenderer } from "./scroll-area-renderer";
// Phase 14: Lottie (post-M2) — final canonical atom renderer (47/47 coverage)
export { LottieRenderer } from "./lottie-renderer";
export {
  RadioGroupRenderer,
  RadioGroupItemRenderer,
} from "./radio-group-renderer";
export {
  BrandLogoRenderer,
  CartLineRenderer,
  CartLinesRenderer,
  CheckoutStepRenderer,
  CommerceButtonRenderer,
  ConfirmationDisplayRenderer,
  CopyrightRenderer,
  HeroBannerRenderer,
  ImageGalleryRenderer,
  LinkColumnsRenderer,
  LogoRenderer,
  MenuItemRenderer,
  MenuListRenderer,
  PriceDisplayRenderer,
  ProductCardRenderer,
  SemanticHeadingRenderer,
  SocialIconsRenderer,
  StepIndicatorRenderer,
  TotalsSummaryRenderer,
} from "./semantic-property-renderers";
